import { isFunction } from "lodash-es";

import { keysToSnakeCase } from "../../utils/keys-to-snake-case";

const valueOrFunction = <T>(value: T | (() => T)): T => (isFunction(value) ? value() : value);

export type UnknownRecord = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: {
      push?: (data: unknown) => void;
    };
  }
}

export const createEvent =
  <Data extends UnknownRecord | undefined = undefined>(
    event: string,
    initialValues?: UnknownRecord | (() => UnknownRecord),
  ) =>
  (...args: Data extends undefined ? [] : [Data]) =>
    pushGtmEvent({
      event,
      site_area: "b2b",
      ...keysToSnakeCase(valueOrFunction(initialValues)),
      ...keysToSnakeCase(args[0]),
    });

// ----------------------------------------------------------------------------
// Page-view ordering: queue every dataLayer push until the page's `page_view`
// event fires, then flush the queue in arrival order. Ensures `page_view` is
// always the first tracking event of any page (initial load and SPA navigation).
//
// Without this, React's bottom-up effect order means child effects (e.g.
// view_item_list from CatalogList) push BEFORE the root useTrackPageViews fires
// page_view. GTM/GA4 attribute these earlier events to the wrong page context.
// ----------------------------------------------------------------------------

// Failsafe: if page_view never fires within this window (e.g. component
// unmounted before `useTrackPageViews` could push), flush queued events so
// we don't silently drop them. Kept short because our own initial page_view
// is scheduled 100 ms after mount — anything meaningfully longer than that
// means something went wrong and we'd rather flush than stall.
const PAGE_VIEW_FLUSH_TIMEOUT_MS = 300;

type PendingPush = { path: string; item: UnknownRecord };

let pageViewFiredForPath: string | null = null;
const pendingPushes: PendingPush[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const currentPath = (): string => (typeof window !== "undefined" ? window.location.pathname : "");

// Flush queued items belonging to `path`. Items queued for any other path are dropped — they
// were intended to be attached to a page the user has already navigated away from, so flushing
// them here would mis-attribute them to the current path. Wrong attribution is worse than
// data loss.
const flushPending = (path: string) => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  for (const pending of pendingPushes) {
    if (pending.path === path) {
      window.dataLayer?.push?.(pending.item);
    }
  }
  pendingPushes.length = 0;
};

const armFailsafeTimer = () => {
  if (flushTimer) return;
  // Capture the path at arming time. If the user has navigated by the time this fires,
  // flushPending(armedPath) will discard items rather than attach them to the new page.
  const armedPath = currentPath();
  flushTimer = setTimeout(() => flushPending(armedPath), PAGE_VIEW_FLUSH_TIMEOUT_MS);
};

const pushOrQueue = (item: UnknownRecord, isPageView: boolean) => {
  const path = currentPath();
  if (isPageView) {
    window.dataLayer?.push?.(item);
    pageViewFiredForPath = path;
    flushPending(path);
    return;
  }

  if (pageViewFiredForPath === path) {
    window.dataLayer?.push?.(item);
    return;
  }

  pendingPushes.push({ path, item });
  armFailsafeTimer();
};

export function pushGtmEvent(data: { event: string; [key: string]: unknown }) {
  pushOrQueue(
    {
      site_area: "b2b",
      ...keysToSnakeCase(data),
    },
    data.event === "page_view",
  );
}

export const pushClearEcommerceEvent = () => {
  pushOrQueue({ ecommerce: null }, false);
};
